/**
 * Idle-session expiry.
 *
 * A booking page left open for hours shows stale rates and availability (and,
 * after a deploy, a stale bundle). Once the guest has been inactive for
 * IDLE_LIMIT_MS we put them back on a fresh search — their dates, guests and
 * promo are kept, so the search re-runs on its own and the prices are live.
 *
 * Never fires while a payment is in flight (see setPaymentInProgress), and the
 * guard only runs on the search and checkout routes — a finished booking
 * (confirmation / booking-details) and the payment page are left alone.
 */

// 30 minutes is the industry norm for a booking session: it is what GA4 calls a
// new session, and OTAs/airlines re-check rates somewhere between 15 and 30.
// Master switch for the whole feature. OFF: the code ships with the build and does
// nothing until this is flipped.
//
// It switches off the BEHAVIOUR, not just the notice. With this false the guard adds
// no listeners, runs no interval and never redirects, so an idle guest is simply left
// alone. A flag that only hides a label while the behaviour carries on underneath is
// the trap logged as INC-091 — not repeated here.
//
// To enable: set this to true. Nothing else needs changing.
export const IDLE_EXPIRY_ENABLED = false;

export const IDLE_LIMIT_MS = 30 * 60 * 1000; // 30 minutes of no activity

// sessionStorage flag so the notice survives the reload/redirect
export const REFRESH_NOTICE_KEY = 'bw_idle_refreshed';

let lastActivityAt = Date.now();
let paymentInProgress = false;

export const markActivity = () => { lastActivityAt = Date.now(); };
export const getIdleMs = () => Date.now() - lastActivityAt;
export const resetIdle = () => { lastActivityAt = Date.now(); };

/** Checkout sets this while a booking/payment request is in flight. */
export const setPaymentInProgress = (value) => { paymentInProgress = !!value; };
export const isPaymentInProgress = () => paymentInProgress;

/**
 * Idle limit, with an opt-in override for testing: ?idle_test=<seconds>.
 * Harmless in production — it only shortens the window.
 */
export const getIdleLimitMs = (searchParams) => {
  const raw = searchParams?.get?.('idle_test');
  const seconds = Number(raw);
  if (raw && Number.isFinite(seconds) && seconds >= 5) return seconds * 1000;
  return IDLE_LIMIT_MS;
};

// Params worth carrying to the fresh search: the search itself, the hotel,
// display preferences and tracking. Deliberately dropped: one-time tokens and
// anything that belongs to a specific booking (ltkn, sessionId, bookingID...).
const KEEP_PARAMS = [
  'hotel_id', 'accommodationid', 'group_id', 'domain',
  'checkin', 'checkout', 'adult', 'child', 'childAges',
  'promo', 'ratecode', 'currency', 'lang', 'multilanguageid',
  'wcid', 'profileid',
  // Attribution must survive — dropping gclid/srsltid would break the hotel's
  // Google Ads conversion tracking, and Google Hotel Ads deep links carry
  // ratecode + srsltid (see utils/urlParams.js).
  'utm_source', 'utm_medium', 'utm_campaign', '_ga', 'gclid', 'srsltid',
  'idle_test', // testing override, so a second expiry can be tried straight away
];

const todayISO = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/**
 * Build the fresh-search URL from the current params.
 * Dates already in the past are dropped so the search page falls back to its
 * own defaults instead of searching a date that can no longer be booked.
 */
export const buildFreshSearchUrl = (searchParams) => {
  const next = new URLSearchParams();
  KEEP_PARAMS.forEach((key) => {
    const value = searchParams?.get?.(key);
    if (value) next.set(key, value);
  });

  const checkin = next.get('checkin');
  if (checkin && checkin < todayISO()) {
    next.delete('checkin');
    next.delete('checkout');
  }

  const query = next.toString();
  return query ? `/?${query}` : '/';
};
